import { forwardRef, useEffect } from "react";
import { useIntl } from "react-intl";
import { useStateMachine } from "little-state-machine";
import { Route, Routes, useParams } from "react-router-dom";

import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { updateShipmentId } from "store/store";
import ShipmentRouteRedirection from "components/organisms/ShipmentRouteRedirection/ShipmentRouteRedirection";
import Wrapper from "components/organisms/Wrapper/Wrapper";
import ShipmentDetails from "pages/shipper/ShipmentDetails/ShipmentDetails";
import ShipmentBilling from "pages/common/ShipmentBilling/ShipmentBilling";
import { getProviderCustomLabels } from "api";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";
import { ShipmentTrackingContextProvider } from "pages/common/ShipmentTracking/ShipmentTrackingContext";
import ShipmentTrackingV2 from "pages/common/ShipmentTrackingV2/ShipmentTrackingV2";
import ShipmentQuotesV2 from "pages/provider/ShipmentQuotesV2/ShipmentQuotesV2";
import CustomsInvoiceBanner from "../ShipmentTracking/components/CustomsInvoiceBanner";
import QuotationView from "./QuotationView";
import QuotationForm from "../QuotationForm/QuotationForm";

const QuotationDashboard = forwardRef(() => {
  const intl = useIntl();
  const { state, actions }: any = useStateMachine({ updateShipmentId });
  const { id: shipmentIdUrl } = useParams();
  const { shipmentId } = state as any;
  const isQuickRequest = document.location.pathname.includes("quick");
  const [isCustomBroker] = useCustomBrokerPermission(isQuickRequest);

  const {
    data: shipmentData,
    isInitialLoading: isLoading,
    refetch,
    error: shipmentBasicError,
  } = useGetShipmentBasic(shipmentIdUrl!, {
    enabled: !isQuickRequest && !!shipmentIdUrl && /(S|QR)\d+/g.test(shipmentIdUrl),
    staleTime: 45 * 1000, // 45 seconds
  });

  const customBrokerWasInvited = shipmentData?.shipment?.is_customs_broker_invited_provider || false;

  const isOffPlatform = (shipmentData as any)?.shipment?.is_off_platform_shipment;
  const showQuotesRoute = !isCustomBroker || (isCustomBroker && customBrokerWasInvited);
  const showCustomsInvoiceBanner = shipmentData?.shipment?.shipment_status === "delivered" && isCustomBroker;

  useEffect(() => {
    if (["S", "Q"].includes(shipmentIdUrl!?.charAt(0)) && (!shipmentId || shipmentId !== shipmentIdUrl)) {
      actions.updateShipmentId({
        shipmentId: shipmentIdUrl,
      });
    }
  }, [shipmentIdUrl]);

  // get provider field custom label
  useEffect(() => {
    if (shipmentId) {
      getProviderCustomLabels(shipmentId).then((res) => localStorage.setItem("user_custom_label", JSON.stringify(res.data)));
    }
  }, [shipmentId]);

  return (
    <Routes>
      {showQuotesRoute && (
        <>
          <Route
            path=""
            element={
              <Wrapper
                pagekey={`/shipment/${shipmentIdUrl}`}
                tabTitle={intl.formatMessage({
                  id: "quote.quoteRequestsTabTitle",
                  defaultMessage: "Quote Requests",
                })}
                shipmentIdOnTab
                component={
                  <QuotationView
                    isQuickRequest={isQuickRequest}
                    shipmentData={shipmentData}
                    shipmentBasicError={shipmentBasicError}
                  />
                }
                isLoading={isLoading}
                isProviderQuotationPage
              />
            }
          />
          <Route
            path="/v2"
            element={
              <Wrapper
                pagekey={`/shipment/${shipmentIdUrl}/v2`}
                tabTitle={intl.formatMessage({
                  id: "quote.quoteRequestsTabTitle",
                  defaultMessage: "Quote Requests",
                })}
                shipmentIdOnTab
                component={
                  <ShipmentQuotesV2
                    isQuickRequest={isQuickRequest}
                    fetchShipment={refetch}
                    shipmentData={shipmentData}
                    shipmentBasicError={shipmentBasicError}
                  />
                }
                isLoading={isLoading}
                isProviderQuotationPage
              />
            }
          />
        </>
      )}
      <Route
        path="/tracking"
        element={
          <>
            {showCustomsInvoiceBanner && <CustomsInvoiceBanner />}
            <ShipmentTrackingContextProvider>
              <Wrapper
                pagekey={`/shipment/${shipmentIdUrl}/tracking`}
                tabTitle={intl.formatMessage({
                  id: "general.tracking",
                  defaultMessage: "Tracking",
                })}
                shipmentIdOnTab
                component={<ShipmentTrackingV2 isOffPlatform={isOffPlatform} />}
                isLoading={isLoading}
                isProviderTrackingPage
              />
            </ShipmentTrackingContextProvider>
          </>
        }
      />
      <Route
        path="/detail"
        element={
          <Wrapper
            pagekey={`/shipment/${shipmentIdUrl}/detail`}
            tabTitle={intl.formatMessage({
              id: "quote.quoteDetailsTabTitle",
              defaultMessage: "Quote Details",
            })}
            shipmentIdOnTab
            component={<ShipmentDetails />}
            isLoading={isLoading}
          />
        }
      />
      <Route
        path="/billing/*"
        element={
          <Wrapper
            pagekey={`/shipment/${shipmentIdUrl}/billing`}
            tabTitle={intl.formatMessage({
              id: "quote.billingsTabTitle",
              defaultMessage: "Billing",
            })}
            shipmentIdOnTab
            component={<ShipmentBilling />}
            isLoading={isLoading}
          />
        }
      />
      <Route
        path="/quotation/add"
        element={
          <Wrapper
            pagekey={`/shipment/${shipmentIdUrl}/quotation/add`}
            hideTopNav
            tabTitle="New Quotation"
            component={<QuotationForm />}
          />
        }
      />
      {/* Always keep this last item in the file.. */}
      <Route path="*" element={<ShipmentRouteRedirection />} />
    </Routes>
  );
});

export default QuotationDashboard;
